<template>
  <v-container id="create_edit_product" fluid tag="section">
    <base-v-component :heading="isDialogCreateProduct ? $t('create-product') : $t('edit-product')" />

    <template>
      <v-card class="mx-auto px-8">
        <v-form>
          <v-container v-if="isLoading">
            <v-row>
              <v-col>
                <v-progress-linear
                  :active="isLoading"
                  :indeterminate="true"
                  height="4"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container v-if="!isLoading">
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="editingObject.productName"
                  :label="$t('product-name')"
                  required
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.productArticleNumber"
                  :label="$t('article-number')"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-menu
                  ref="menuDate"
                  v-model="menuDate"
                  :close-on-content-click="false"
                  :return-value.sync="editingObject.deliveryDate"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="deliveryDateFormatted"
                      label="Delivery date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="deliveryDateFormatted"
                    no-title
                    scrollable
                    class="my-0"
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menuDate = false">Cancel</v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.menuDate.save(editingObject.deliveryDate)"
                    >OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  v-model="editingObject.category"
                  :items="projectCategories"
                  append-icon
                  label="Category"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="editingObject.status"
                  :items="status"
                  label="Status"
                >
                  <template v-slot:selection="{ item }">{{ $t(item) }}</template>
                  <template slot="item" slot-scope="data">{{ $t(data.item) }}</template>
                </v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.productClass"
                  label="Class of cosmetic product"
                  hint="e.g. 'Body lotion'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.areaOfApplication"
                  label="Surface area/sites of application"
                  hint="e.g. 'Whole body'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.durationOfUse"
                  label="Duration of Use"
                  hint="e.g. 'Leave-on', 'Rinse-off'"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.surfaceOfApplication"
                  label="Surface area(s) of application"
                  hint="e.g. '0.5 cm2'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.exposureRoute"
                  label="Exposure route"
                  hint="e.g. 'Dermal (skin)'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editingObject.targetPopulation"
                  label="Target user population"
                  hint="e.g. 'Adults'"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="editingObject.volume"
                  :rules="[rules.numeric]"
                  :label="$t('volume') + ' (ml/g)'"
                  type="number"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  v-model.number="editingObject.SEDconstant"
                  :items="seds"
                  :clearable="true"
                  label="SED constant (select or write custom)"
                  :messages="''+editingObject.SEDconstant"
                  :return-object="false"
                >
                  <template
                    slot="item"
                    slot-scope="data"
                  >{{data.item.text}} - {{ data.item.value }}</template>
                </v-combobox>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="editingObject.templatePath"
                  :items="templates"
                  label="Default template"
                  item-text="name"
                  item-value="fullPath"
                >
                </v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-combobox
                  multiple
                  v-model="editingObject.tags"
                  :items="projectTags"
                  label="Tags"
                  append-icon
                  chips
                  dense
                  deletable-chips
                  class="tag-input"
                ></v-combobox>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea 
                  v-model="editingObject.comments" 
                  auto-grow 
                  label="Comments"
                  dense
                  rows="1" 
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedIngredient"
                  :items="searchItems"
                  :loading="searchLoading"
                  :filter="filterNotSelected"
                  item-text="inciName"
                  item-value="cosingRef"
                  :label="$t('search') +' '+ $t('evaluations')"
                  flat
                  solo-inverted
                  hide-no-data
                  hide-selected
                  hide-details
                  return-object
                  @update:search-input="querySearch"
                >
                  <template slot="selection">
                    <span class="purple--text">{{ selectIngredientMessage }}</span>
                  </template>
                  <!-- 
                  <template
                    slot="item"
                    slot-scope="data"
                  >
                    <v-icon small class="mr-2" v-if="data.item.evaluationId">mdi-check</v-icon>
                    <v-icon small color="red" class="mr-2" v-if="!data.item.evaluationId">mdi-exclamation-thick</v-icon>
                     {{ data.item.inciName }} ({{data.item.casNumber}})
                  </template>
                  -->
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
          <v-container v-if="editingObject.ingredients.length">
            <h3>Ingredients</h3>
            <v-row
              align="center"
              justify="center"
              v-for="(item,index) in editingObject.ingredients.slice().reverse()"
              :key="index"
            >
              <v-col
                class="col-4 text-left text-truncate clickable"
                @click="onClickIngredient(item)"
              >
                <v-tooltip v-if="!item.evaluationId && !item.id" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small color="red" class="mr-2">mdi-exclamation-thick</v-icon>
                  </template>
                  <span>No safety evaluation</span>
                </v-tooltip>
                <v-tooltip v-if="(item.evaluationId || item.id) && !item.assessment" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small class="mr-2" color="accent">mdi-exclamation-thick</v-icon>
                  </template>
                  <span>Evaluated, but lacks safety assessment</span>
                </v-tooltip>
                <v-tooltip v-if="(item.evaluationId || item.id) && item.assessment" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small class="mr-2">mdi-check</v-icon>
                  </template>
                  <span>Safety evaluated</span>
                </v-tooltip>
                <v-tooltip v-if="isProhibited(item)" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small class="mr-2" color="red">mdi-alert</v-icon>
                  </template>
                  <span>Ingredient is prohibited (Annex 2)</span>
                </v-tooltip>
                <v-tooltip v-if="isRestricted(item)" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small class="mr-2">mdi-alert-circle-outline</v-icon>
                  </template>
                  <span>Ingredient has restriction (Annex 3)</span>
                </v-tooltip>
                {{ item.inciName }}
              </v-col>
              <v-col cols="2">
                <v-text-field 
                  v-model.number="item.weight"
                  :rules="[rules.numeric]"
                  type="number" 
                  label="Quantity (%)" 
                  :hint="`Quantity left: ` + (100 - (getWeightSum | 0))"
                />
              </v-col>
              <v-col cols="1">
                <v-text-field disabled v-model.number="item.dermalAbsorptionPcnt" label="Dermal Abs. (%)" type="number" />
              </v-col>
              <v-col cols="1">
                <v-text-field disabled :value="calculateSED(item).toFixed(4)" label="SED" type="number"  />
              </v-col>
              <v-col cols="1">
                <v-text-field disabled v-model.number="item.noael" label="NOAEL" type="number" />
              </v-col>
              <v-col cols="2">
                <v-text-field disabled :value="calculateMoS(item).toFixed(2)" label="MoS" type="number" />
              </v-col>
              <v-col cols="1" class="text-right">
                <v-icon small class="mr-2" @click="onDeleteIngredient(item)">mdi-trash-can</v-icon>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2" offset="4">
                <span class="overline">Total:</span> <span class="overline" :class="getWeightSum>100 ? 'red--text' : ''">{{ getWeightSum }}% </span>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card>
    </template>

    <v-row 
      v-if="!isLoading"
      align="center" 
      justify="center" 
      class="mt-8"
    >
      <v-btn
        v-if="id"
        color="error"
        class="mr-10"
        rounded
        outlined
        @click="onDeleteItem"
      >
        {{ $t('delete') }}
      </v-btn>
      <!--
      <v-btn
        v-if="id"
        color="action"
        class="mr-10"
        rounded
        @click="onCopyItem"
      >
        {{ $t('copy') }}
      </v-btn>
      -->
      <v-btn
        v-if="id"
        color="action"
        class="mr-10"
        rounded
        @click="onCreateReport"
      >
        {{ $t('generate-report') }}
      </v-btn>
      <v-btn 
        color="primary" 
        class="mr-0" 
        rounded 
        @click="saveProduct"
      >
        {{ $t('save') }}
      </v-btn>
    </v-row>

    <!-- DIALOG CONFIRM DELETE -->
    <v-dialog v-model="dialogConfirmDelete" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('delete-product') }}</v-card-title>
        <v-card-text>{{ $t('delete-product-confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogConfirmDelete = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" text @click="deleteProduct">{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG REPORT -->
    <v-dialog v-model="dialogCreateReport" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t('create-report') }}</v-card-title>
        <v-card-text>
          <v-container>
            <p>{{ $t('create-report-description')}}</p>
            <v-row>
              <v-col>
                <v-select
                  v-model="editingObject.templatePath"
                  :items="templates"
                  :label="$t('template')"
                  item-text="name"
                  item-value="fullPath"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCreateReport = false">{{ $t('cancel') }}</v-btn>
          <v-btn primary text @click="createReport">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Data from '@/utils/Data';
import Fuse from 'fuse.js'

export default {
  name: "CreateEditProduct",

  data: () => ({
    id: null,
    isLoading: false,
    search: "",
    dialogCreateReport: false,
    dialogConfirmDelete: false,
    dialogProduct: false,
    isDialogCreateProduct: true,
    editingObject: {},
    searchItems: [],
    searchConsolidated: [],
    searchLoading: false,
    selectedIngredient: null,
    selectIngredientMessage: "",
    status: [
      "WAITING_FOR_DOCUMENTATION",
      "DOCUMENTATION_READY",
      "IN_PROGRESS",
      "COMPLETED",
    ],
    rules: {
      required: value => !!value || 'Required.',
      numeric: value => !isNaN(value) || 'Invalid number.',
      email: value => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return pattern.test(value) || 'Invalid e-mail.'
      },
    },
    //date: new Date().toISOString().substr(0, 10),
    menuDate: false,
    duplicate: false,

    fuse: null,
    fuseOptions: {
      keys: ['inciName', 'casNumber']
    }
  }),

  created() {
    this.resetEditingObject();
    if (this.$route.params.id) {
      this.id = this.$route.params.id;
      this.duplicate = this.$route.params.duplicate ? true : false;
      this.isLoading = true;
      if (this.activeProject && this.activeProject.id) {
        this.initEditView(this.id);
      }
    }
    this.fuse = new Fuse(this.cosing, this.fuseOptions)
  },

  watch: {
    selectedIngredient: function (newSelection) {
      if (newSelection) {

        let index = null;
        if (newSelection.objectID) {
          index = this.editingObject.ingredients.findIndex(
            (item) => item.id === newSelection.objectID
          );
        }
        else { 
          index = this.editingObject.ingredients.findIndex(
            (item) => item.reference === newSelection.reference
          );
        }

        if (index < 0) {
          this.addToIngredientsList(newSelection);
          this.$nextTick(() => {
            newSelection = null;
            //ingredientToAdd = null;
            this.selectIngredientMessage = "";
          });
        } else {
          this.selectIngredientMessage = "Ingredient have already been added";
        }
      }
    },

    activeProject: function () {
      this.fetchProduct();
    },
  },

  computed: {
    user() {
      return this.$store.getters["user/getUser"];
    },
    company() {
      return this.$store.getters['company/getCompany']
    },
    access() {
      return this.$store.getters["company/getAccess"];
    },
    activeProject() {
      const currentProject = this.$store.getters["projects/getActiveProject"];
      return currentProject;
    },
    products() {
      return this.$store.getters["products/getProducts"];
    },
    cosing() {
      return this.$store.getters["ingredients/getCosing"];
    },
    projectIngredients() {
      return this.$store.getters["projectIngredients/getProjectIngredients"];
    },
    projectTags() {
      return this.$store.getters["projects/getTags"];
    },
    projectCategories() {
      return this.$store.getters["projects/getCategories"];
    },
    templates() {
      return this.$store.getters["templates/getTemplates"];
    },
    seds() {
      return Data.getSED();
    },
    getWeightSum() {
      let total = 0; 
      this.editingObject.ingredients.forEach(item => total += parseInt(item.weight | 0))
      return total;
    },
    deliveryDateFormatted: {
      get() {
        const dt = this.editingObject.deliveryDate;
        return dt ? this.$moment(this.editingObject.deliveryDate).format('YYYY-MM-DD') : '';
      },
      set(value) {
        this.editingObject.deliveryDate = value;
      }
    },
  },

  methods: {
    resetEditingObject() {
      this.editingObject = {
        productName: "",
        productArticleNumber: "",
        category: "",
        status: "",
        comments: "",
        deliveryDate: "",
        productClass: "",
        areaOfApplication: "",
        durationOfUse: "",
        surfaceOfApplication: "",
        exposureRoute: "",
        targetPopulation: "",
        tags: [],
        ingredients: [],
        volume: "",
        SEDconstant: null,
        templatePath: "",
      };
    },
    async initEditView(id) {
      this.isDialogCreateProduct = false;
      const projectId = this.activeProject.id;
      const item = this.$store.getters["products/getProduct"](id);

      if (item) {
        this.setEditingObject(item);
        this.$store.dispatch("templates/fetchTemplates", projectId);
      }
    },
    fetchProduct() {
      this.$store
        .dispatch("products/fetchProduct", { 
          projectId: this.activeProject.id,
          itemId: this.id,
        })
        .then(() => {
          this.initEditView(this.id);
        });
    },
    fetchProjectIngredient(id) {
      this.$store
        .dispatch("projectIngredients/fetchProjectIngredient", { 
          projectId: this.activeProject.id,
          itemId: id,
        })
    },
    async querySearch(search) {
      if (search && search.length >= 2) {
        this.$searchService.querySearch('project_ingredients', search)
          .then((res) => {
            this.searchItems = res;
          })
      }
    },
    filterNotSelected(item) {
      const res = this.editingObject.ingredients.some((ingredient) => ingredient.id === item.objectID);
      return !res;
    },
    isProhibited (item) {
      return this.$store.getters["ingredients/isProhibited"](item);
    },
    isRestricted (item) {
      return this.$store.getters["ingredients/isRestricted"](item);
    },
    async hasEvaluation (item) {
      return await this.$store.dispatch('projectIngredients/hasEvaluation', { projectId: this.activeProject.id, item: item })
    },
    hasProjectIngredient(item) {
      if (!this.projectIngredients) return false;
      const index = this.projectIngredients.findIndex(
        (obj) => obj.reference === item.reference
      );
      return index > -1 ? true : false;
    },
    getProjectIngredient(item) {
      return this.projectIngredients.find((obj) => {
        if (obj.reference === item.reference) {
          return obj;
        }
      });
    },
    calculateSED(item) {
      const k = this.editingObject.SEDconstant
        ? this.editingObject.SEDconstant
        : 0;
      const quantity = item.weight | 0;
      const dermal = item.dermalAbsorptionPcnt | 0;
      const sed = eval(k * (quantity / 100) * (dermal / 100));
      item.sed = sed.toFixed(4);
      return sed;
    },
    calculateMoS(item) {
      const calcSED = this.calculateSED(item);
      const noael = item.noael | 0;
      let mos = 0;
      if (noael && calcSED) {
        mos = eval(noael / calcSED);
      }
      item.mos = mos.toFixed(4);
      return mos;
    },
    customFilter(item, queryText) {
      const textOne = item.inciName.toLowerCase();
      const textTwo = item.casNumber.toLowerCase();
      const searchText = queryText.toLowerCase();
      return (
        textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1
      );
    },
    async setEditingObject(item) {
      this.editingObject = JSON.parse(JSON.stringify(item));

      if (this.duplicate) {
        delete this.editingObject.id;
        this.isDialogCreateProduct = true;
      }

      const updatedIngredients = await Promise.all(this.editingObject.ingredients
        .map(async (ingredient) => {
          let evaluation
          if (ingredient.id) {
            evaluation = await this.$store.dispatch('projectIngredients/fetchProjectIngredient', { projectId: this.activeProject.id, itemId: ingredient.id })
          } else {
            evaluation = await this.$store.dispatch('projectIngredients/hasEvaluation', { projectId: this.activeProject.id, item: ingredient })
          }
          return Object.assign(ingredient, evaluation)
        }
      ));
      this.editingObject.ingredients = updatedIngredients;
      this.isLoading = false;
    },
    async addToIngredientsList (newSelection) {
      const id = newSelection.id || newSelection.objectID;
      const projectIngredient = await this.$store.dispatch('projectIngredients/fetchProjectIngredient', { projectId: this.activeProject.id, itemId: id })
      projectIngredient.id = projectIngredient.id || id;
      this.editingObject.ingredients.push(JSON.parse(JSON.stringify(projectIngredient)))
    },
    onCreateItem() {
      if (!this.activeProject) {
        this.$notify({
          group: "base",
          type: "error",
          title: "No active Workspace",
          text: "You need to select a Workspace, or create one in Account",
        });
        return null;
      }
      if (!this.access.products) {
        this.$notify({
          group: "base",
          type: "error",
          title: "No access left",
          text: "You need to upgrade your account",
        });
        return null;
      }
      this.isDialogCreateProduct = true;
      this.dialogProduct = true;
    },
    onDeleteItem() {
      this.dialogConfirmDelete = true;
    },
    onCopyItem() {
      console.log('copy')
    },
    onCreateReport() {
      this.dialogCreateReport = true;
    },
    onClickIngredient(item) {
      if (item.id || item.evaluationId) {
        this.$router.push({name: 'evaluations.edit',  params: { id: item.id || item.evaluationId }})
      }
      else if (item.reference) {
        this.$router.push({name: 'ingredients.view',  params: { id: item.reference }})
      }
    },
    onDeleteIngredient(ingredient) {
      const index = this.editingObject.ingredients.findIndex(
        (item) => item.reference === ingredient.reference
      );
      if (index > -1) {
        this.editingObject.ingredients.splice(index, 1);
      }
    },
    saveProduct() {
      if (this.isDialogCreateProduct) {
        this.createProduct();
      } else {
        this.editProduct();
      }
    },
    createProduct() {
      if (this.editingObject.productName !== "" && this.activeProject.id) {
        const item = this.editingObject;
        item.projectId = this.activeProject.id;
        item.companyId = this.company.id;
        item.createdBy = this.user.id;
        item.updatedBy = this.user.id;

        this.$store.dispatch("products/createProduct", item).then(() => {
          this.$router.push("/products");
        });
      } else {
        this.$notify({
          group: "base",
          type: "error",
          title: "No active Workspace",
          text: "You need to select a Workspace, or create one in Account",
        });
      }
    },
    editProduct() {
      if (this.editingObject.productName !== "" && this.editingObject.projectId) {
        const item = this.editingObject;
        item.updatedBy = this.user.id;

        this.$store
          .dispatch(
            "products/editProduct",
            JSON.parse(JSON.stringify(item))
          )
          .then(() => {
            this.$notify({
              group: "base",
              type: "success",
              title: "Item updated",
              text: "The product has been successfully updated",
            });
          })
          .catch((error) => {
            console.log(error);
            this.$notify({
              group: "base",
              type: "error",
              title: "Oops, something went wrong",
              text: "The product has not been updated. Please try again.",
            });
          });
      } else {
        this.$notify({
          group: "base",
          type: "error",
          title: "No active Workspace",
          text: "You need to select a Workspace, or create one in Account",
        });
      }
    },
    deleteProduct() {
      this.$store
        .dispatch("products/deleteProduct", this.editingObject)
        .then(() => {
          this.dialogConfirmDelete = false;
          this.$router.replace({ name: "products" });
        })
        .catch((error) => {
          console.log(error);
          this.$notify({
            group: "base",
            type: "error",
            title: "Oops, something went wrong",
            text: "The product has not been deleted. Please try again.",
          });
        });
    },
    createReport() {
      if (this.editingObject.templatePath) {
        this.$store
          .dispatch("reports/generateDoc", this.editingObject)
          .then(() => {
            this.dialogCreateReport = false;
          })
          .catch((error) => {
            console.error(error);
            this.$notify({
              group: "base",
              type: "error",
              title: "Oops, something went wrong",
              text: "That did not go according to plan. Please try again.",
            });
          });
      } else {
        this.$notify({
          group: "base",
          type: "info",
          title: "You forgot something...",
          text: "Pick a template to generate the report",
        });
      }
    },
  },
  /*
  beforeRouteLeave (to, from, next) {
    console.log('removed', to, from, next)
    next();
  }
  */
};
</script>

<style scoped>
.v-chip {
  max-width: 150px !important;
  text-overflow: ellipsis;
  display: inline-block;
}
</style>